import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { Floor } from 'src/api/types';
import {
  BuildingSelect,
  BuildingSelectItem,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import {
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { getDeviceSize } from 'src/store/agent/selectors';
import { fetchFloors } from 'src/store/floors/services';
import { getParkingState } from 'src/store/parking/selectors';
import { openParking, parkingActions } from 'src/store/parking/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize, PlaceType } from 'src/types';

import { ParkingBookingDateField } from '../../ParkingBookingSettings/ParkingBookingDateField';
import { ParkingBookingTimeField } from '../../ParkingBookingSettings/ParkingBookingTimeField';
import { ParkingOfficeSidesheetButton } from '../../ParkingOffice/ParkingOfficeSidesheetButton';

import classes from './DesktopPageActions.module.scss';

const renderValue = (option: Option<string>): string => option.value ?? '';

const filterTownOptions = (option: TownSelectItem): boolean =>
  !!option.value.hasParkings;

const filterBuildingOptions = (option: BuildingSelectItem): boolean =>
  !!option.value.hasParkings;

export const DesktopPageActions: FC = () => {
  const deviceSize = useSelector(getDeviceSize);
  const dispatch = useAppDispatch();
  const { town, building, bookingObjects } = useSelector(getParkingState);

  const handleTownChange = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(parkingActions.setTown(value?.value));
      dispatch(parkingActions.setBuilding(undefined));
      dispatch(parkingActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleBuildingChange = useCallback(
    async (value: BuildingSelectItem | undefined) => {
      let floor: Floor | undefined;
      dispatch(sidesheetActions.close());
      dispatch(parkingActions.setBuilding(value?.value));

      if (value?.value) {
        const floors = await dispatch(
          fetchFloors({
            cached: true,
            buildingId: value.value.buildingId,
          }),
        ).unwrap();
        floor = floors.find(f => f.isParking);
      }

      dispatch(parkingActions.setFloor(floor));
    },
    [dispatch],
  );

  const [parkingSearchValue, setParkingSearchValue] =
    useState<Option<string> | null>(null);
  const [parkingSearchInput, setParkingSearchInput] = useState('');
  const { scrollTo } = useFloorMap();

  const parkingSearchOptions = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of bookingObjects) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [bookingObjects]);

  const filteredParkingSearchOptions = useMemo((): Option<string>[] => {
    const search = parkingSearchInput.toLowerCase();

    return parkingSearchOptions.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [parkingSearchInput, parkingSearchOptions]);

  const handleParkingSearchChange = useCallback(
    async (option: Option<string>) => {
      setParkingSearchValue(option);

      const found = bookingObjects.get(option.id as string);
      if (found && found.type === PlaceType.Parking) {
        dispatch(openParking(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [bookingObjects, dispatch, scrollTo],
  );

  const handleParkingSearchClear = useCallback(() => {
    setParkingSearchValue(null);
  }, []);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );

  return (
    <div className={classes.actions}>
      {deviceSize !== DeviceSize.S && (
        <div className={classes.main}>
          <div className={classes.town}>
            <TownSelect
              town={townSelectItem}
              onChange={handleTownChange}
              filterOptions={filterTownOptions}
            />
          </div>
          <div className={classes.building}>
            <BuildingSelect
              building={buildingSelectItem}
              onChange={handleBuildingChange}
              townId={town?.townId}
              disabled={!town}
              filterOptions={filterBuildingOptions}
              key={town?.townId}
            />
          </div>
        </div>
      )}
      <div className={classes.additional}>
        {deviceSize === DeviceSize.S && <ParkingOfficeSidesheetButton small />}
        <div className={classes.date}>
          <ParkingBookingDateField />
        </div>
        <div className={classes.time}>
          <ParkingBookingTimeField />
        </div>
        <div className={classes.controls}>
          <div className={classes.search}>
            <Autocomplete
              placeholder='Номер'
              type='search'
              size='medium'
              disabled={!parkingSearchOptions.length}
              options={filteredParkingSearchOptions}
              value={parkingSearchValue}
              noOptionsText='не найдено'
              onChange={handleParkingSearchChange}
              renderValue={renderValue}
              onInputChange={setParkingSearchInput}
              onInputClear={handleParkingSearchClear}
              loading={!parkingSearchOptions.length}
              fullWidth
            />
          </div>
        </div>
      </div>
    </div>
  );
};
